/**
 * ZoomControls - Zoom control panel for page builder preview
 *
 * Provides zoom in/out controls, preset zoom levels, and fit-to-window functionality.
 * Designed to work with the page builder preview panel.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Minus, Plus, Maximize2, ChevronDown } from 'lucide-react';

/**
 * Preset zoom levels for the dropdown
 */
const ZOOM_PRESETS = [25, 50, 75, 100, 125, 150, 200];

/**
 * ZoomControls - Control panel for zoom operations
 *
 * @param {Object} props
 * @param {number} props.zoomLevel - Current zoom level (percentage, e.g., 100)
 * @param {Function} props.onZoomChange - Callback when zoom changes: (newZoomLevel) => void
 * @param {Function} props.onFitToWindow - Callback for fit to window action: () => void
 * @param {number} [props.minZoom=25] - Minimum zoom level
 * @param {number} [props.maxZoom=200] - Maximum zoom level
 * @param {number} [props.step=10] - Zoom increment step
 * @param {string} [props.className] - Additional CSS classes
 */
export default function ZoomControls({
  zoomLevel,
  onZoomChange,
  onFitToWindow,
  minZoom = 25,
  maxZoom = 200,
  step = 10,
  className = '',
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  // Close dropdown on escape key
  useEffect(() => {
    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isDropdownOpen]);

  const handleZoomIn = () => {
    const newZoom = Math.min(zoomLevel + step, maxZoom);
    onZoomChange(newZoom);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(zoomLevel - step, minZoom);
    onZoomChange(newZoom);
  };

  const handlePresetSelect = (preset) => {
    onZoomChange(preset);
    setIsDropdownOpen(false);
  };

  const handleFitToWindow = () => {
    onFitToWindow();
    setIsDropdownOpen(false);
  };

  const handleResetZoom = () => {
    onZoomChange(100);
    setIsDropdownOpen(false);
  };

  const isAtMinZoom = zoomLevel <= minZoom;
  const isAtMaxZoom = zoomLevel >= maxZoom;

  const buttonBaseClasses = `
    p-2
    inline-flex items-center justify-center
    border border-gray-300
    transition-colors duration-150
    focus:z-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-0
  `;

  const buttonEnabledClasses = 'bg-white text-gray-700 hover:bg-gray-50';
  const buttonDisabledClasses = 'bg-gray-100 text-gray-400 cursor-not-allowed';

  return (
    <div className={`inline-flex items-center ${className}`}>
      {/* Zoom controls group */}
      <div className="inline-flex rounded-md shadow-sm" role="group">
        {/* Zoom out button */}
        <button
          type="button"
          onClick={handleZoomOut}
          disabled={isAtMinZoom}
          className={`
            ${buttonBaseClasses}
            rounded-l-md
            ${isAtMinZoom ? buttonDisabledClasses : buttonEnabledClasses}
          `}
          title="Zoom out"
          aria-label="Zoom out"
        >
          <Minus className="w-4 h-4" />
        </button>

        {/* Zoom level indicator with dropdown */}
        <div className="relative">
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`
              ${buttonBaseClasses}
              -ml-px
              min-w-[70px]
              bg-white text-gray-700 hover:bg-gray-50
            `}
            title="Select zoom level"
            aria-label="Select zoom level"
            aria-expanded={isDropdownOpen}
            aria-haspopup="listbox"
          >
            <span className="text-sm font-medium">{Math.round(zoomLevel)}%</span>
            <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown menu */}
          {isDropdownOpen && (
            <div
              ref={dropdownRef}
              className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-36 bg-white border border-gray-300 rounded-md shadow-lg z-50"
              role="listbox"
            >
              <div className="py-1">
                {/* Preset zoom levels */}
                {ZOOM_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => handlePresetSelect(preset)}
                    className={`
                      w-full px-3 py-1.5 text-left text-sm
                      transition-colors duration-150
                      ${preset === zoomLevel
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                    role="option"
                    aria-selected={preset === zoomLevel}
                  >
                    {preset}%
                  </button>
                ))}

                {/* Divider */}
                <div className="border-t border-gray-200 my-1" />

                {/* Reset to 100% */}
                <button
                  type="button"
                  onClick={handleResetZoom}
                  className="w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                >
                  Reset to 100%
                </button>

                {/* Fit to window */}
                <button
                  type="button"
                  onClick={handleFitToWindow}
                  className="w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150 flex items-center gap-2"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                  Fit to window
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Zoom in button */}
        <button
          type="button"
          onClick={handleZoomIn}
          disabled={isAtMaxZoom}
          className={`
            ${buttonBaseClasses}
            -ml-px
            ${isAtMaxZoom ? buttonDisabledClasses : buttonEnabledClasses}
          `}
          title="Zoom in"
          aria-label="Zoom in"
        >
          <Plus className="w-4 h-4" />
        </button>

        {/* Fit to window button */}
        <button
          type="button"
          onClick={onFitToWindow}
          className={`
            ${buttonBaseClasses}
            -ml-px
            rounded-r-md
            ${buttonEnabledClasses}
          `}
          title="Fit to window"
          aria-label="Fit to window"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * ZoomControlsCompact - Minimal zoom controls (just +/- and percentage)
 *
 * @param {Object} props
 * @param {number} props.zoomLevel - Current zoom level (percentage)
 * @param {Function} props.onZoomChange - Callback when zoom changes
 * @param {number} [props.minZoom=25] - Minimum zoom level
 * @param {number} [props.maxZoom=200] - Maximum zoom level
 * @param {number} [props.step=10] - Zoom increment step
 * @param {string} [props.className] - Additional CSS classes
 */
export function ZoomControlsCompact({
  zoomLevel,
  onZoomChange,
  minZoom = 25,
  maxZoom = 200,
  step = 10,
  className = '',
}) {
  const handleZoomIn = () => {
    const newZoom = Math.min(zoomLevel + step, maxZoom);
    onZoomChange(newZoom);
  };

  const handleZoomOut = () => {
    const newZoom = Math.max(zoomLevel - step, minZoom);
    onZoomChange(newZoom);
  };

  const isAtMinZoom = zoomLevel <= minZoom;
  const isAtMaxZoom = zoomLevel >= maxZoom;

  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <button
        type="button"
        onClick={handleZoomOut}
        disabled={isAtMinZoom}
        className={`
          p-1 rounded
          transition-colors duration-150
          ${isAtMinZoom
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-gray-600 hover:bg-gray-100'
          }
        `}
        title="Zoom out"
        aria-label="Zoom out"
      >
        <Minus className="w-3.5 h-3.5" />
      </button>

      <span className="text-xs font-medium text-gray-600 min-w-[40px] text-center">
        {Math.round(zoomLevel)}%
      </span>

      <button
        type="button"
        onClick={handleZoomIn}
        disabled={isAtMaxZoom}
        className={`
          p-1 rounded
          transition-colors duration-150
          ${isAtMaxZoom
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-gray-600 hover:bg-gray-100'
          }
        `}
        title="Zoom in"
        aria-label="Zoom in"
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/**
 * ZoomIndicator - Simple zoom level display (no controls)
 *
 * @param {Object} props
 * @param {number} props.zoomLevel - Current zoom level
 * @param {string} [props.className] - Additional CSS classes
 */
export function ZoomIndicator({ zoomLevel, className = '' }) {
  return (
    <span className={`text-xs font-medium text-gray-600 ${className}`}>
      {Math.round(zoomLevel)}%
    </span>
  );
}
